/**
 * Where the page is and where it goes next — the `window.location` reads and moves every app used to
 * write by hand (ralint R004, 2026-10-20).
 *
 * Plain functions, not hooks: they are for the code that runs OUTSIDE a component or before the
 * router exists — the boot-time language (`?lang=`), a route table computed from the path, a
 * redirect to another app, a download. Inside a component under `<BrowserRouter>` the router's own
 * `useSearchParams` / `useLocation` (or isis' `useUrlState`) are the reactive answer; reading
 * `window.location` there renders a stale value after a client-side navigation.
 *
 * Each is SSR-safe: outside a browser the reads answer empty and the moves do nothing.
 */

const browser = (): boolean => typeof window !== 'undefined' && Boolean(window.location);

/** One query parameter of the current URL (`?test=true` → `searchParam('test') === 'true'`), or `null`. */
export function searchParam(name: string): string | null {
  return browser() ? new URLSearchParams(window.location.search).get(name) : null;
}

/** The whole query string, `?` included (`''` when there is none). */
export function search(): string {
  return browser() ? window.location.search : '';
}

/** The current path (`/meals/42`), `''` outside a browser. */
export function pathname(): string {
  return browser() ? window.location.pathname : '';
}

/** The full current URL — what a "come back here after signing in" link carries. */
export function currentUrl(): string {
  return browser() ? window.location.href : '';
}

/** This page's origin (`https://time.martincastroalvarez.com`) — a postMessage target, a redirect URI base. */
export function origin(): string {
  return browser() ? window.location.origin : '';
}

/** Go to `url`, keeping this page in the history (a download, a sign-in, another app). */
export function go(url: string): void {
  if (browser()) window.location.assign(url);
}

/** Leave for `url`, REPLACING this page in the history — a moved app, a redirect nobody should Back into. */
export function leave(url: string): void {
  if (browser()) window.location.replace(url);
}

/** Load the page again (a new deploy's chunks, a session that changed under it). */
export function reload(): void {
  if (browser()) window.location.reload();
}
